import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@shared/ui/table";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@shared/ui/chart";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  Legend,
  Cell,
  LineChart,
  Line,
  PieChart,
  Pie,
} from "recharts";
import {
  BarChart3,
  ChartPie,
  Database,
  Check,
  AlertTriangle,
  X,
  CircleDot,
  CalendarDays,
} from "lucide-react";
import {
  useDailyValidationTrend,
  useMeterConditionDashboard,
  useReadCycleValidationStats,
  useValidationStats,
} from "../../hooks";

import { getRemoteUtilityId } from "@shared/utils/getUtilityId";

export const ReadingEfficiency = ({meterConditionsData, dailyValidationTrend, validationStats, readCycleValidationStats}) => {
  const remoteUtilityId = getRemoteUtilityId();

  const conditionData = meterConditionsData

  const dailyValidationData = dailyValidationTrend

  const validationStatsData = validationStats;

  const readCycleValidationStatsData = readCycleValidationStats
  // Get current month and year
  const getCurrentMonthYear = () => {
    const now = new Date();
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return `${monthNames[now.getMonth()]} ${now.getFullYear()}`;
  };

  // Mock data for meter conditions to include normal, RCNT, faulty and missing
  const meterConditions = [
    {
      condition: "Normal",
      count: conditionData?.meterConditions?.normal?.count,
      percentage: conditionData?.meterConditions?.normal?.percentage,
      icon: Check,
      color: "#10B981",
    },
    {
      condition: "RCNT",
      count: conditionData?.meterConditions?.rcnt?.count,
      percentage: conditionData?.meterConditions?.rcnt?.percentage,
      icon: CircleDot,
      color: "#9b87f5",
    },
    {
      condition: "Faulty",
      count: conditionData?.meterConditions?.faulty?.count,
      percentage: conditionData?.meterConditions?.faulty?.percentage,
      icon: AlertTriangle,
      color: "#F59E0B",
    },
    {
      condition: "Missing",
      count: conditionData?.meterConditions?.pending?.count,
      percentage: conditionData?.meterConditions?.pending?.percentage,
      icon: X,
      color: "#EF4444",
    },
  ];

  // New mock data for read cycle validation progress
  const readCycleData =
    readCycleValidationStatsData?.cycleMetrics?.map((item) => ({
      name: item?.cycleName,
      validated: item?.validatedReadings,
      total: item?.totalReadings,
      percentage: item?.validationPercentage,
    })) || [];

  // Calculate total meters
  const totalMeters = conditionData?.totalMeters;
  const maxValidatedReadCount =
    readCycleValidationStatsData?.maxValidatedReadCount;

  // Colors for the chart - fixed the ChartConfig type by using proper object format
  const colors = {
    validated: { color: "#10B981" },
    total: { color: "#E5DEFF" },
    percentage: { color: "#3B82F6" },
  };

  // Helper function to format percentage for display
  const formatPercentage = (value) => `${value.toFixed(1)}%`;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="page-subheading">
          Reading Efficiency - {getCurrentMonthYear()}
        </h2>
      </div>

      {/* New section for daily validation tracking - Modified layout to place Daily Validation Trend beside Read Cycle Validation */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Daily Validation Stats */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-2">
            <CardTitle className="card-heading-text flex items-center gap-2">
              <CalendarDays className="h-5 w-5" />
              Validation Statistics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex flex-col">
                <span className="card-value-text text-[#10B981]">
                  {validationStatsData?.overallValidationRate || 0}%
                </span>
                <span className="text-sm text-muted-foreground">
                  Overall Validation Rate
                </span>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Total Readings</span>
                  <span className="font-medium">
                    {validationStatsData?.totalReadings}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Validated Readings</span>
                  <span className="font-medium">
                    {validationStatsData?.validatedReadings}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Pending Validation</span>
                  <span className="font-medium">
                    {validationStatsData?.pendingValidation}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Today's Validation Rate</span>
                  <span className="font-medium">
                    {validationStatsData?.todayStats
                      ?.todayValidationRate}
                    %
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Moved both chart sections to be side by side in this grid row */}
        {/* Read Cycle Validation Chart */}
        <Card className="lg:col-span-1 pl-0">
          <CardHeader className="pb-2 pl-2">
            <CardTitle className="card-heading-text flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Read Cycle Validation
            </CardTitle>
          </CardHeader>
          <CardContent className="overflow-hidden">
            <ChartContainer className="h-[250px] overflow-hidden" config={colors}>
              <BarChart
                data={readCycleData}
                margin={{ top: 10, right: 30, left: 0, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" />
                <YAxis
                  yAxisId="left"
                  domain={[maxValidatedReadCount || "dataMax"]}
                />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Legend />
                <Bar
                  yAxisId="left"
                  dataKey="validated"
                  name="Validated Readings"
                  fill={colors.validated.color}
                  radius={[4, 4, 0, 0]}
                />
                <Bar
                  yAxisId="left"
                  dataKey="total"
                  name="Total Readings"
                  fill={colors.total.color}
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ChartContainer>
          </CardContent>
        </Card>

        {/* Daily Validation Trend - Moved to be beside Read Cycle Validation */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-2">
            <CardTitle className="card-heading-text flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Daily Validation Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer className="h-[250px]" config={colors}>
              <LineChart
                data={dailyValidationData}
                margin={{ top: 10, right: 30, left: 0, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" />
                <YAxis yAxisId="left" />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tickFormatter={formatPercentage}
                />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Legend />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="percentage"
                  name="Validation %"
                  stroke={colors.percentage.color}
                />
              </LineChart>
            </ChartContainer>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Meter Conditions Table */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="card-heading-text flex items-center gap-2">
              Meter Conditions Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Condition</TableHead>
                  <TableHead className="text-right">Count</TableHead>
                  <TableHead className="text-right">Percentage</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {meterConditions.map((condition) => (
                  <TableRow key={condition.condition}>
                    <TableCell className="flex items-center gap-2">
                      {condition.icon && (
                        <condition.icon size={16} color={condition.color} />
                      )}
                      {condition.condition}
                    </TableCell>
                    <TableCell className="text-right">
                      {condition.count}
                    </TableCell>
                    <TableCell className="text-right">
                      {condition.percentage}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell className="font-medium">Total Meters</TableCell>
                  <TableCell className="text-right font-medium">
                    {totalMeters}
                  </TableCell>
                  <TableCell className="text-right font-medium">100%</TableCell>
                </TableRow>
              </TableBody>
            </Table>
            <div className="mt-4 text-center">
              <a
                href="#"
                className="text-sm text-[#9b87f5] hover:underline flex items-center justify-center gap-1"
              >
                View Detailed Report
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Distribution Card */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-2">
            <CardTitle className="card-heading-text flex items-center gap-2">
              <ChartPie className="h-5 w-5" />
              Meter Condition Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[200px] flex">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  layout="vertical"
                  data={meterConditions}
                  margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    horizontal={true}
                    vertical={false}
                  />
                  <XAxis type="number" hide />
                  <YAxis dataKey="condition" type="category" width={100} />
                  <Tooltip />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                    {meterConditions.map((entry, index) => {
                      return <Cell key={`cell-${index}`} fill={entry.color} />;
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
